const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sync.direct';

interface ApiResponse<T> {
  data?: T;
  error?: string;
  /** HTTP status code from the response. Undefined for network errors. */
  statusCode?: number;
}

/**
 * Returns a fresh token on success, or null if the session can't be refreshed
 * (e.g. user banned/suspended mid-session, or unknown user). Registered by
 * UserContext, which owns the SignalR ReconnectUser silent-refresh path.
 */
type RefreshHandler = () => Promise<string | null>;

class ApiService {
  private token: string | null = null;
  private refreshHandler: RefreshHandler | null = null;
  private onAuthCleared: (() => void) | null = null;
  // Shared in-flight refresh so concurrent 401s trigger exactly one ReconnectUser.
  private refreshInFlight: Promise<string | null> | null = null;

  setToken(token: string | null) {
    // Reject a structurally-invalid JWT rather than sending a bad Authorization
    // header on every call (US-4 AC-2 — no malformed-token dead-ends at startup).
    if (token && !this.looksLikeJwt(token)) {
      console.warn('[ApiService] Ignoring malformed token');
      this.token = null;
      return;
    }
    this.token = token;
  }

  /** Register the silent-refresh path (ReconnectUser) used by the 401 interceptor. */
  setRefreshHandler(handler: RefreshHandler | null) {
    this.refreshHandler = handler;
  }

  /** Called when refresh fails and auth must be cleared (route to login). */
  setOnAuthCleared(handler: (() => void) | null) {
    this.onAuthCleared = handler;
  }

  private looksLikeJwt(token: string): boolean {
    return token.split('.').length === 3;
  }

  /**
   * Returns the bearer Authorization header for the current token, or
   * null if no token is set. Callers that need to bypass `request()`
   * (e.g. multipart uploads that mustn't set `Content-Type: application/json`)
   * use this to attach the same auth their JSON calls would carry.
   */
  getAuthorizationHeader(): string | null {
    return this.token ? `Bearer ${this.token}` : null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<ApiResponse<T>> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`;
      if (import.meta.env.DEV) {
        console.debug('[ApiService] Sending authenticated request to:', endpoint);
      }
    } else {
      console.warn('[ApiService] No token set for request to:', endpoint);
    }

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
        credentials: 'include',
      });

      // 401 interceptor: attempt exactly ONE silent refresh (ReconnectUser) then retry once.
      // Loop-guarded by `isRetry` so a still-401 response after refresh ends in a clean
      // logout, never an infinite refresh/retry spin (US-4 AC-1/AC-2).
      if (response.status === 401 && !isRetry && this.refreshHandler) {
        const newToken = await this.refreshOnce();
        if (newToken) {
          return this.request<T>(endpoint, options, true);
        }
        // Refresh failed (banned/suspended/unknown): clear auth and route to login.
        this.token = null;
        this.onAuthCleared?.();
        return { error: 'Session expired. Please sign in again.', statusCode: 401 };
      }

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        return {
          error: errorBody.message || `Request failed with status ${response.status}`,
          statusCode: response.status,
        };
      }

      const data = await response.json();
      return { data, statusCode: response.status };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /** Run at most one ReconnectUser refresh at a time; concurrent 401s share its result. */
  private async refreshOnce(): Promise<string | null> {
    if (!this.refreshHandler) return null;
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.refreshHandler()
        .catch((e) => {
          console.warn('[ApiService] Silent refresh failed:', e);
          return null;
        })
        .finally(() => {
          this.refreshInFlight = null;
        }) as Promise<string | null>;
    }
    const token = await this.refreshInFlight;
    if (token) this.setToken(token);
    return this.token;
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiService = new ApiService();
